import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SnackService } from '../snack.service';
import { LoadingService } from '../loading.service';
import BoardType from 'src/app/model/product/board/board-type';
import IPBoardPlacementType from 'src/app/model/product/ip-board/ip-board-placement-type';
import {
  SIGN_PLACEMENT_PRICE_TYPE_CHOICES,
  SignPlacementPriceType,
} from 'src/app/model/sign/sign-placement-price/sign-placement-price-type';
import { SignMeasurements } from 'src/app/model/sign/enums/sign-measurements';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { IP_BOARD_PART_TYPE_CHOICES } from 'src/app/model/product/ip-board/ip-board-part-type';
import { SignHelper } from 'src/app/helpers/sign-helper';
import { SignTypesChoices } from 'src/app/model/sign/enums/sign-type';
import { SignDetail } from 'src/app/model/sign/sign-detail';
import { Observable } from 'rxjs';
import { _post } from '../base.service';

@Injectable({
  providedIn: 'root',
})
export class SignPlacementPriceCalculationRequestService {
  private static BASE_URL = '/signplacementprices';
  private readonly post: <T>(
    path: string,
    body: SignPlacementPriceCalculationRequest,
    deserialize: (response: any) => T
  ) => Observable<T>;
  private readonly deserialize = (res) => res as number;

  constructor(
    http: HttpClient,
    snackService: SnackService,
    loadingService: LoadingService,
    router: Router
  ) {
    const requestServices = {
      http,
      loadingService,
      router,
      snackService,
    };
    this.post = _post(
      SignPlacementPriceCalculationRequestService.BASE_URL,
      requestServices
    );
  }

  public calculatePlacementPriceForBoardPart(
    ipBoardPart: IPBoardPart,
    companyId: number
  ): Promise<number> {
    const request = this.getRequest(ipBoardPart, companyId);
    return request == null
      ? Promise.resolve(null)
      : this.post(
          '/calculatePlacementPrice',
          request,
          this.deserialize
        ).toPromise();
  }

  private getRequest(
    ipBoardPart: IPBoardPart,
    companyId: number
  ): SignPlacementPriceCalculationRequest {
    if (companyId == null) {
      return null;
    }
    const signPlacementPriceType = this.getSignPlacementPriceType(ipBoardPart);
    if (signPlacementPriceType == null) {
      return null;
    }
    if (
      signPlacementPriceType.ordinal ===
        SIGN_PLACEMENT_PRICE_TYPE_CHOICES.IP_BOARD.ordinal &&
      (ipBoardPart.boardType == null || ipBoardPart.placementType == null)
    ) {
      return null;
    }
    const signMeasurements = this.getSignMeasurements(ipBoardPart);
    if (signMeasurements == null) {
      return null;
    }
    return {
      signPlacementPriceType: signPlacementPriceType,
      signMeasurements: signMeasurements,
      boardType:
        signPlacementPriceType.ordinal ===
        SIGN_PLACEMENT_PRICE_TYPE_CHOICES.ADHESIVE_BOARD.ordinal
          ? null
          : ipBoardPart.boardType,
      placementType:
        signPlacementPriceType.ordinal ===
        SIGN_PLACEMENT_PRICE_TYPE_CHOICES.ADHESIVE_BOARD.ordinal
          ? null
          : ipBoardPart.placementType,
      companyId: companyId,
    };
  }

  private getSignPlacementPriceType(
    ipBoardPart: IPBoardPart
  ): SignPlacementPriceType {
    return ipBoardPart.sign != null
      ? this.getSignPlacementPriceTypeFromSign(ipBoardPart.sign)
      : ipBoardPart.otherIPBoardPartType == null
      ? null
      : ipBoardPart.otherIPBoardPartType.ordinal ===
          IP_BOARD_PART_TYPE_CHOICES.IP_70_100.ordinal ||
        ipBoardPart.otherIPBoardPartType.ordinal ===
          IP_BOARD_PART_TYPE_CHOICES.IP_100_140.ordinal
      ? SIGN_PLACEMENT_PRICE_TYPE_CHOICES.IP_BOARD
      : ipBoardPart.otherIPBoardPartType.ordinal ===
        IP_BOARD_PART_TYPE_CHOICES.ADHESIVE_BOARD.ordinal
      ? SIGN_PLACEMENT_PRICE_TYPE_CHOICES.ADHESIVE_BOARD
      : SignHelper.areStandardIPBoardDimensions(
          ipBoardPart.otherWidth,
          ipBoardPart.otherHeight
        )
      ? SIGN_PLACEMENT_PRICE_TYPE_CHOICES.IP_BOARD
      : null;
  }

  private getSignPlacementPriceTypeFromSign(
    sign: SignDetail
  ): SignPlacementPriceType {
    return sign.type == null
      ? null
      : sign.type.value === SignTypesChoices.INPRESS.value
      ? SIGN_PLACEMENT_PRICE_TYPE_CHOICES.IP_BOARD
      : sign.type.value === SignTypesChoices.STICKER.value
      ? SIGN_PLACEMENT_PRICE_TYPE_CHOICES.ADHESIVE_BOARD
      : null;
  }

  private getSignMeasurements(ipBoardPart: IPBoardPart): SignMeasurements {
    return ipBoardPart.sign != null
      ? ipBoardPart.sign.signMeasurements
      : SignHelper.getMatchingSignMeasurements(
          ipBoardPart.otherWidth,
          ipBoardPart.otherHeight
        );
  }
}

interface SignPlacementPriceCalculationRequest {
  signPlacementPriceType: SignPlacementPriceType;
  signMeasurements: SignMeasurements;
  boardType: BoardType | null;
  placementType: IPBoardPlacementType | null;
  companyId: number;
}
